import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const ConnectionCard = ({ user, viewMode = 'grid', onConnectionRequest }) => {
  const [isRequestSent, setIsRequestSent] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!user) return null;

  const partner = user?.partners?.[0];
  const socialLinks = user?.partner_social_links || [];
  const specialties = user?.partner_musical_specialties || [];

  const sendConnectionRequest = async () => {
    try {
      setLoading(true);

      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) return;

      const { error } = await supabase?.from('professional_connections')?.insert({
          requester_id: session?.user?.id,
          recipient_id: user?.id,
          connection_message: 'Bonjour ! J\'aimerais me connecter avec vous sur Artis.io pour élargir mon réseau professionnel artistique.'
        });

      if (error) throw error;

      setIsRequestSent(true);
      onConnectionRequest?.(user?.id);
    } catch (error) {
      console.error('Error sending connection request:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPartnerTypeLabel = (partnerType) => {
    const labels = {
      'agent': 'Agent',
      'venue': 'Venue',
      'booker': 'Booker',
      'independent': 'Artiste indépendant'
    };
    return labels?.[partnerType] || partnerType;
  };

  const getProfessionLabel = (profession) => {
    const professions = {
      'musician': 'Musicien',
      'singer': 'Chanteur',
      'dj': 'DJ',
      'producer': 'Producteur',
      'model': 'Modèle',
      'actor': 'Acteur',
      'dancer': 'Danseur',
      'performer': 'Artiste'
    };
    return professions?.[profession] || profession;
  };

  const getMatchScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-blue-600 bg-blue-100';
    if (score >= 40) return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getTotalFollowers = () => {
    return socialLinks?.reduce((total, link) => total + (link?.follower_count || 0), 0);
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-start space-x-4">
          {/* Avatar */}
          <Link to={`/enhanced-artist-profile`} className="flex-shrink-0">
            {user?.avatar_url ? (
              <img
                src={user?.avatar_url}
                alt={partner?.business_name || user?.full_name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                <span className="text-primary-foreground font-semibold text-lg">
                  {(partner?.business_name || user?.full_name)?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
            )}
          </Link>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <Link 
                    to={`/enhanced-artist-profile`}
                    className="text-lg font-semibold text-foreground hover:text-accent transition-colors"
                  >
                    {partner?.business_name || user?.full_name}
                  </Link>
                  {partner?.is_verified && (
                    <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                      <Icon name="Check" size={12} color="white" />
                    </div>
                  )}
                  {/* Match Score */}
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${getMatchScoreColor(user?.matchScore)}`}>
                    {user?.matchScore}% compatibilité
                  </div>
                </div>

                <div className="text-muted-foreground mb-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <span>{getPartnerTypeLabel(partner?.partner_type)}</span>
                    {partner?.artist_profession && (
                      <>
                        <span>•</span>
                        <span>{getProfessionLabel(partner?.artist_profession)}</span>
                      </>
                    )}
                    {partner?.city && (
                      <>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          <Icon name="MapPin" size={12} />
                          <span>{partner?.city}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Description */}
                {partner?.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {partner?.description}
                  </p>
                )}

                {/* Stats */}
                <div className="flex items-center space-x-4 text-xs text-muted-foreground mb-3">
                  {partner?.total_bookings > 0 && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={12} />
                      <span>{partner?.total_bookings} réservations</span>
                    </div>
                  )}
                  {partner?.average_rating > 0 && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={12} />
                      <span>{partner?.average_rating}/5</span>
                    </div>
                  )}
                  {getTotalFollowers() > 0 && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Users" size={12} />
                      <span>{getTotalFollowers()?.toLocaleString()} followers</span>
                    </div>
                  )}
                  {user?.mutualConnections > 0 && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Users" size={12} />
                      <span>{user?.mutualConnections} connexions communes</span>
                    </div>
                  )}
                </div>

                {/* Recommendation Reasons */}
                {user?.recommendationReasons?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {user?.recommendationReasons?.map((reason, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center text-xs px-2 py-1 bg-muted text-muted-foreground rounded-full"
                      >
                        {reason?.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Button */}
              <div className="flex-shrink-0 ml-4">
                <Button
                  variant={isRequestSent ? "outline" : "default"}
                  size="sm"
                  onClick={sendConnectionRequest}
                  disabled={isRequestSent || loading}
                  loading={loading}
                  iconName={isRequestSent ? "Check" : "UserPlus"}
                >
                  {isRequestSent ? 'Demande envoyée' : 'Se connecter'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid view (default)
  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <Link to={`/enhanced-artist-profile`} className="flex-shrink-0">
            {user?.avatar_url ? (
              <img
                src={user?.avatar_url}
                alt={partner?.business_name || user?.full_name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                <span className="text-primary-foreground font-semibold text-lg">
                  {(partner?.business_name || user?.full_name)?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
            )}
          </Link>

          {/* Match Score */}
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getMatchScoreColor(user?.matchScore)}`}>
            {user?.matchScore}%
          </div>
        </div>

        {/* User Info */}
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-1">
            <Link 
              to={`/enhanced-artist-profile`}
              className="text-lg font-semibold text-foreground hover:text-accent transition-colors line-clamp-1"
            >
              {partner?.business_name || user?.full_name}
            </Link>
            {partner?.is_verified && (
              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="Check" size={12} color="white" />
              </div>
            )}
          </div>

          <div className="text-muted-foreground text-sm mb-2">
            <div className="flex items-center space-x-2">
              <span>{getPartnerTypeLabel(partner?.partner_type)}</span>
              {partner?.artist_profession && (
                <>
                  <span>•</span>
                  <span>{getProfessionLabel(partner?.artist_profession)}</span>
                </>
              )}
            </div>
            {partner?.city && (
              <div className="flex items-center space-x-1 mt-1">
                <Icon name="MapPin" size={12} />
                <span>{partner?.city}</span>
              </div>
            )}
          </div>

          {/* Description */}
          {partner?.description && (
            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
              {partner?.description}
            </p>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-xs text-muted-foreground">
          {partner?.total_bookings > 0 && (
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={12} />
              <span>{partner?.total_bookings} réservations</span>
            </div>
          )}
          {partner?.average_rating > 0 && (
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={12} />
              <span>{partner?.average_rating}/5</span>
            </div>
          )}
          {getTotalFollowers() > 0 && (
            <div className="flex items-center space-x-1 col-span-2">
              <Icon name="Users" size={12} />
              <span>{getTotalFollowers()?.toLocaleString()} followers</span>
            </div>
          )}
        </div>

        {/* Mutual Connections */}
        {user?.mutualConnections > 0 && (
          <div className="flex items-center space-x-1 text-xs text-muted-foreground mb-4">
            <Icon name="Users" size={12} />
            <span>{user?.mutualConnections} connexions communes</span>
          </div>
        )}

        {/* Recommendation Reasons */}
        {user?.recommendationReasons?.length > 0 && (
          <div className="space-y-1 mb-4">
            {user?.recommendationReasons?.slice(0, 2)?.map((reason, index) => (
              <div key={index} className="text-xs text-muted-foreground">
                {reason?.type === 'verified' && '✓ '}
                {reason?.type === 'location' && '📍 '}
                {reason?.type === 'role' && '💼 '}
                {reason?.type === 'activity' && '🔥 '}
                {reason?.type === 'profession' && '🎵 '}
                {reason?.text}
              </div>
            ))}
          </div>
        )}

        {/* Specialties */}
        {specialties?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {specialties?.slice(0, 3)?.map((specialty, index) => (
              <span
                key={index}
                className="inline-flex text-xs px-2 py-1 bg-accent/10 text-accent border border-accent/20 rounded-full"
              >
                {specialty?.specialty}
              </span>
            ))}
            {specialties?.length > 3 && (
              <span className="text-xs text-muted-foreground px-2 py-1">
                +{specialties?.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-2">
          <Button
            variant={isRequestSent ? "outline" : "default"}
            size="sm"
            onClick={sendConnectionRequest}
            disabled={isRequestSent || loading}
            loading={loading}
            iconName={isRequestSent ? "Check" : "UserPlus"}
            className="w-full"
          >
            {isRequestSent ? 'Demande envoyée' : 'Se connecter'}
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            iconName="MessageCircle"
          >
            Message
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConnectionCard;